import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Puzzle } from "lucide-react";

const MODULES = [
    { id: "acquisizione", label: "Acquisizione Privati" },
    { id: "nuovi_privati", label: "Nuovi Privati" },
    { id: "next_seller", label: "Next Seller" },
];
const EMPTY = { free: false, custom_price: "", extra_trial_months: 0, attivato: false };

const statusLabel = (m) => {
    if (!m) return "Non attivo";
    if (m.reason === "included") return "Modulo attivo (incluso VD Manager)";
    if (m.reason === "free") return "Modulo attivo (gratuito)";
    if (m.reason === "admin") return "Modulo attivo";
    if (m.reason === "trial") return `In prova fino al ${new Date(m.trial_ends_at).toLocaleDateString("it-IT")}`;
    if (m.reason === "paid") return `Modulo attivo · scade ${m.expires_at ? new Date(m.expires_at).toLocaleDateString("it-IT") : ""}`;
    if (m.reason === "trial_expired") return "Prova terminata · non attivo";
    return "Non attivo";
};
const statusTone = (m) => {
    if (!m) return "text-ink-400";
    if (["included", "free", "admin", "paid"].includes(m.reason)) return "text-emerald-400";
    if (m.reason === "trial") return "text-blue-300";
    if (m.reason === "trial_expired") return "text-amber-300";
    return "text-ink-400";
};

export const AdminAddonOverrides = React.forwardRef(function AdminAddonOverrides({ userId }, ref) {
    const [form, setForm] = useState({ acquisizione: { ...EMPTY }, nuovi_privati: { ...EMPTY }, next_seller: { ...EMPTY } });
    const [modules, setModules] = useState({});
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get(`/admin/agencies/${userId}/addon-overrides`).then((r) => {
            const ov = r.data.overrides || {};
            const next = {};
            for (const m of MODULES) {
                const o = ov[m.id] || {};
                next[m.id] = { ...EMPTY, ...o, custom_price: o.free ? 0 : (o.custom_price ?? "") };
            }
            setForm(next);
            setModules(r.data.modules || {});
        }).catch(() => {}).finally(() => setLoaded(true));
    }, [userId]);

    React.useImperativeHandle(ref, () => ({
        save: async () => {
            if (!loaded) return;
            const payload = {};
            for (const m of MODULES) {
                const f = form[m.id];
                const price = f.custom_price === "" || f.custom_price === null ? null : Number(f.custom_price);
                const free = price === 0;
                payload[m.id] = {
                    free,
                    attivato: !!f.attivato,
                    custom_price: free ? null : price,
                    extra_trial_months: Number(f.extra_trial_months) || 0,
                };
            }
            await api.put(`/admin/agencies/${userId}/addon-overrides`, payload);
        },
    }), [form, loaded, userId]);

    const set = (id, patch) => setForm((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    const addFreeMonthAll = () => setForm((prev) => {
        const next = {};
        for (const m of MODULES) next[m.id] = { ...prev[m.id], extra_trial_months: Math.min(36, (Number(prev[m.id].extra_trial_months) || 0) + 1) };
        return next;
    });
    const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2 text-ink-50 focus:border-gold outline-none text-sm";

    return (
        <div data-testid="admin-addon-overrides" className="mt-6 border-t border-white/10 pt-6">
            <div className="flex items-center gap-2 mb-1">
                <Puzzle size={14} className="text-gold" />
                <span className="label-eyebrow">Moduli aggiuntivi</span>
            </div>
            <p className="text-[11px] text-ink-400 mb-4">Prezzo standard 99€/mese con 1 mese di prova. Le modifiche sono immediate per l'agenzia.</p>
            <div className="space-y-3">
                {MODULES.map((m) => {
                    const f = form[m.id];
                    const isFree = f.custom_price !== "" && f.custom_price !== null && Number(f.custom_price) === 0;
                    return (
                        <div key={m.id} data-testid={`addon-override-${m.id}`} className="border border-white/10 p-4 space-y-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="text-sm text-ink-50">{m.label}</div>
                                <div data-testid={`addon-status-${m.id}`} className={`text-[11px] font-medium ${statusTone(modules[m.id])}`}>{loaded ? statusLabel(modules[m.id]) : "Caricamento…"}</div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-ink-400 mb-1">Prezzo personalizzato (€/mese)</div>
                                    <input
                                        data-testid={`addon-price-${m.id}`}
                                        type="number" min="0" step="0.01"
                                        placeholder="99 (standard)"
                                        value={f.custom_price}
                                        onChange={(e) => set(m.id, { custom_price: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-ink-400 mb-1">Mesi gratuiti aggiuntivi</div>
                                    <input
                                        data-testid={`addon-extra-months-${m.id}`}
                                        type="number" min="0" max="36" step="1"
                                        value={f.extra_trial_months}
                                        onChange={(e) => set(m.id, { extra_trial_months: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>
                                <div className="pb-2 text-right sm:text-left">
                                    {isFree && <span data-testid={`addon-free-label-${m.id}`} className="text-xs font-medium text-emerald-400">Gratuito (nessun pagamento)</span>}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <button
                type="button"
                data-testid="addon-add-free-months-all"
                onClick={addFreeMonthAll}
                className="mt-3 w-full border border-gold/40 py-2.5 text-xs font-medium text-gold hover:bg-gold/10 transition-colors"
            >
                + Aggiungi mesi gratuiti (1 mese su tutti i moduli)
            </button>
        </div>
    );
});
